import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {
    useProviderConnectionsInfiniteQuery,
    useProviderConnectionsSearch,
} from './useProviderConnectionsQuery';
import {ProviderConnection} from '../types';

// FNP-1248 — the aggregating list hook the table consumes.

// The DEFAULT is a per-entity judgement and stays local; HOW two values compare
// does not (that is `sortRecords`).
//
// `displayName` rather than a timestamp: it is what the tenant named the
// connection and the only field they can predict the order of. A tenant with
// four connections is scanning for one by name, not by when it was made.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<ProviderConnection> = {
    columnAccessor: 'displayName',
    direction: 'asc',
};

/**
 * ⚠️ THE SEARCHABLE FIELDS ARE CHOSEN, NOT SWEPT.
 *
 * `displayName`, `providerId` and `status` — the three things visible in the
 * table and therefore the three a tenant would search by.
 *
 * DELIBERATELY EXCLUDED:
 *   `id`         — opaque and composite ('PROVIDER_CONNECTION#<acct>#<uuid>').
 *                  Making it a search key trains people to treat it as
 *                  something to paste around, and it embeds the account id.
 *   `externalId` — the tenant's trust-policy value. Not a name, and surfacing
 *                  it through a keyword match would scatter it into contexts
 *                  that have no explanation attached.
 *   `statusDetail` — a provider's verbatim error message. Long, unstable, and
 *                  matching a substring of it produces results a tenant cannot
 *                  account for.
 */
export const matchesKeyword = (item: ProviderConnection, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.displayName,
        item.providerId,
        item.status,
    ];
    return haystack.some(value =>
        typeof value === 'string' && value.toLowerCase()
                                          .includes(needle));
};

export const useProviderConnections = () => {

    const query = useProviderConnectionsInfiniteQuery();

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword, which is
    // shared with the search input in a sibling component (FNP-815).
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<ProviderConnection>>(DEFAULT_SORT_STATUS);

    const {keyword} = useProviderConnectionsSearch();

    const pages = query.data?.pages ?? [];

    const items: ProviderConnection[] = pages.flatMap(
        page => (page && 'items' in page) ? page.items : [],
    ).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Sorting and keyword are CLIENT-SIDE, over the pages fetched so far.
    // Neither can reach rows the browser has not loaded. That is a property of
    // token pagination, not an oversight — and it is why the footer states a
    // LOADED count rather than a total.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    return {
        // The loaded set BEFORE keyword narrowing — the window the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        // The raw token is never exposed; the sentinel is what
        // DataTableTokenPagination wants.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        isReloading: query.isRefetching,
    };
};
